import {
  Box,
  Button,
  CircularProgress,
  FormHelperText
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as constants from "../../../../constants";
import environment from "../../../../environments/environment";
import { RootState } from "../../../../store/store";
import { axiosInterceptor } from "../../services/authInterceptor";
import { StyledButtonContainer } from "../../StyledComponents/Button";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
} from "../../StyledComponents/Container";
import { notifyUser } from "../../Utils/notifyUser";
import "./BlackBaud.css";

const BlackBaudConfiguration: React.FC = () => {
  const userDetails = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [popup, setPopup] = useState<Window | null>(null);
  const sessionIdRef = useRef(crypto.randomUUID());

  useEffect(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = crypto.randomUUID();
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "BLACKBAUD_AUTH_SUCCESS" && event.data?.code) {
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    getBlackBaudAuthConnection();
  }, []);

  const getBlackBaudAuthConnection = async () => {
    setLoading(true);
    try {
      const response = await axiosInterceptor.get(
        `${environment.getBlackBaudAuthCode}?clientId=${userDetails.clientId}`
      );

      if (response && response.status === constants.STATUS_CODES.SUCCESS) {
        const isConnected = response.data?.data?.isConnected ?? true;
        setIsAuthenticated(isConnected);
      } else {
        setIsAuthenticated(false);
      }
    } catch (error: any) {
      setIsAuthenticated(false);
      notifyUser(
        error?.response?.data?.message,
        "error",
        translate("blackbaudConnection")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async () => {
    try {
      const width = 700;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      const redirectUri = encodeURIComponent(environment.BlackBaudRedirectUrl);
      const authUrl = `https://oauth2.sky.blackbaud.com/authorization?client_id=${environment.blackBaudClientId}&response_type=code&redirect_uri=${redirectUri}`;
      const params = new URLSearchParams({
        client_id: "your-client-id",
        response_type: "code",
        redirect_uri: `${window.location.origin}/bb-auth-redirect`,
        state: sessionIdRef.current,
      });

      const authWindow = window.open(
        `${authUrl}`,
        "BlackBaudAuth",
        `width=${width},height=${height},left=${left},top=${top}`
      );

      setPopup(authWindow);
    } catch (error) {
      notifyUser(translate("failedToOpenBlackbaudAuthWindow"), "error", translate("blackbaudConnection"));
    }
  };

  // ✅ NEW useEffect to listen for popup success message
  useEffect(() => {
    const handleAuthSuccess = async (event: MessageEvent) => {
      if (event.data?.type === "BLACKBAUD_AUTH_SUCCESS") {
        const code = event.data?.code;
        if (code) {
          try {
            const url = `${environment.postBlackBaudAuthCode}${code}`;
            const response = await axiosInterceptor.post(url, { code });
            notifyUser(response?.data.message, "success", translate("blackbaudConnection")
            );
          } catch (err) {
            notifyUser(
              translate("failedToNotifyBackendOfBlackbaudAuth"),
              "error",
              translate("blackbaudConnection")
            );
          }
        }
        if (code && popup && !popup.closed) popup.close();
        getBlackBaudAuthConnection();
      } else if (event.data?.type === "BLACKBAUD_AUTH_FAILED") {
        notifyUser(translate("blackbaudAuthenticationFailed"), "error", translate("blackbaudConnection"));
      }
    };

    window.addEventListener("message", handleAuthSuccess);
    return () => window.removeEventListener("message", handleAuthSuccess);
  }, [popup]);

  if (loading) {
    return (
      <StyledParentContainer
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Box sx={{ py: 5 }}>
          <CircularProgress />
        </Box>
      </StyledParentContainer>
    );
  }

  return (
    <StyledParentContainer>
      <StyledOneChildContainer>
        <Box sx={{ width: "100%" }}>
          <SectionTitle>{translate("blackbaudAuthenticationSetupPage")}</SectionTitle>
          <FormHelperText
            sx={{ paddingLeft: "16px", color: "#0A558C", fontSize: "25px" }}
          >
            {translate("welcome")} {userDetails.userName}!
          </FormHelperText>

          <StyledButtonContainer>
            {!isAuthenticated ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAuth}
                disabled={loading}
              >
                {translate("connectToBlackbaud")}
              </Button>
            ) : (
              <div className="auth-result">
                <FormHelperText
                  sx={{
                    textAlign: "center",
                    color: "#2CA01C",
                    fontSize: "25px",
                  }}
                >
                  {translate("blackbaudConnectedSessionReady")}🎉🎉🎉
                  <p
                    style={{
                      textAlign: "center",
                      color: "#000",
                      fontSize: "20px",
                    }}
                  >
                    Please{" "}
                    <a href="#" onClick={handleAuth}>
                      click here
                    </a>{" "}
                    {translate("toReconfigureYourBlackbaudIntegration")}
                  </p>
                </FormHelperText>
              </div>
            )}
          </StyledButtonContainer>
        </Box>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};

export default BlackBaudConfiguration;
